/**
 * Part Repository tab for the Call Off module.
 * Displays catalog parts with supplier assignments, search, pagination,
 * and allows assigning/removing suppliers from parts.
 *
 * Supports:
 * - Bulk assign via checkbox selection + "Assign Suppliers" button
 * - Per-part assign via double-click on a row (opens modal for that part)
 * - Searchable supplier dropdown in both assignment flows
 * - CR-8188: a Warehouse filter, and an Export of every part the filters match
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Loader2, Database,
  Users, UserPlus,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Pagination } from '../../ui/pagination';
import { Checkbox } from '../../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import {
  TableBody, TableHead, TableHeader, TableRow,
} from '../../ui/table';
import { Card, CardContent } from '../../ui/card';
import { toast } from 'sonner';
import { callOffAPI } from '../../../lib/api';
import { partsAPI } from '../../../lib/api/parts';
import type { PartCatalogItem, PartRepositoryStats } from '../../../lib/api/types';
import type { PartSupplierUploadResponse } from '../../../lib/api/types/parts';
import SupplierSearchList, { type SupplierOption } from './SupplierSearchList';
import SupplierAssignmentUploadDialog from './SupplierAssignmentUploadDialog';
import PartRepositoryRow from './PartRepositoryRow';
import PartRepositoryToolbar, { ALL_WAREHOUSES } from './PartRepositoryToolbar';
import { usePartsListExport } from './usePartsListExport';

export default function PartRepositoryTab() {
  const [items, setItems] = useState<PartCatalogItem[]>([]);
  const [stats, setStats] = useState<PartRepositoryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAssigned, setFilterAssigned] = useState<string>('all');
  const [warehouse, setWarehouse] = useState<string>(ALL_WAREHOUSES);
  const [warehouses, setWarehouses] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  // The filters behind the rows on screen, so Export matches them even while
  // a newly typed search is still waiting to load.
  const [shownFilters, setShownFilters] = useState<Record<string, string>>({});

  // Bulk supplier assignment state
  const [selectedParts, setSelectedParts] = useState<Set<number>>(new Set());
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<Set<number>>(new Set());
  const [assigning, setAssigning] = useState(false);

  // Per-part assignment state (double-click modal)
  const [partDialogOpen, setPartDialogOpen] = useState(false);
  const [activePart, setActivePart] = useState<PartCatalogItem | null>(null);
  const [partSelectedSuppliers, setPartSelectedSuppliers] = useState<Set<number>>(new Set());
  const [partAssigning, setPartAssigning] = useState(false);

  // Supplier-assignment Excel upload state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<PartSupplierUploadResponse | null>(null);

  const filterParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (search) params['search'] = search;
    if (filterAssigned === 'assigned') params['is_assigned'] = 'true';
    if (filterAssigned === 'unassigned') params['is_assigned'] = 'false';
    if (warehouse !== ALL_WAREHOUSES) params['warehouse'] = warehouse;
    return params;
  }, [search, filterAssigned, warehouse]);

  const fetchParts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callOffAPI.getParts({ ...filterParams, page, page_size: pageSize });
      setItems(res.data.items);
      setTotalCount(res.data.total_count);
      setShownFilters(filterParams);
    } catch {
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filterParams]);

  // CR-8188: the cards count the chosen warehouse's parts.
  const fetchStats = useCallback(async () => {
    try {
      const res = await callOffAPI.getPartStats(
        warehouse !== ALL_WAREHOUSES ? { warehouse } : undefined,
      );
      setStats(res.data);
    } catch { /* ignore */ }
  }, [warehouse]);

  const fetchWarehouses = useCallback(async () => {
    try {
      const res = await callOffAPI.getPartWarehouses();
      setWarehouses(res.data ?? []);
    } catch { /* the filter stays hidden */ }
  }, []);

  const { exporting, runExport } = usePartsListExport(shownFilters, totalCount);

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await callOffAPI.getSuppliers();
      const list = res.data ?? [];
      setSuppliers(list.map((s: Record<string, number | string>) => ({
        id: s['id'] as number,
        name: (s['name'] as string) || `Supplier ${s['id']}`,
      })));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);
  useEffect(() => { fetchWarehouses(); }, [fetchWarehouses]);

  useEffect(() => {
    const timer = setTimeout(fetchParts, 300);
    return () => clearTimeout(timer);
  }, [fetchParts]);

  useEffect(() => { setPage(1); }, [search, filterAssigned, warehouse]);

  const togglePart = (id: number) => {
    setSelectedParts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedParts.size === items.length) {
      setSelectedParts(new Set());
    } else {
      setSelectedParts(new Set(items.map(i => i.id)));
    }
  };

  // Bulk assign handler
  const handleBulkAssign = async () => {
    if (selectedParts.size === 0 || selectedSuppliers.size === 0) return;
    setAssigning(true);
    try {
      await callOffAPI.bulkAssignSuppliers(
        Array.from(selectedParts),
        Array.from(selectedSuppliers),
      );
      toast.success(`Assigned ${selectedSuppliers.size} supplier(s) to ${selectedParts.size} part(s)`);
      setAssignDialogOpen(false);
      setSelectedParts(new Set());
      setSelectedSuppliers(new Set());
      fetchParts();
      fetchStats();
    } catch {
      toast.error('Failed to assign suppliers');
    } finally {
      setAssigning(false);
    }
  };

  // Per-part assign handler (double-click row)
  const handleRowDoubleClick = (part: PartCatalogItem) => {
    setActivePart(part);
    setPartSelectedSuppliers(new Set(part.suppliers.map(s => s.id)));
    setPartDialogOpen(true);
  };

  const handlePartAssign = async () => {
    if (!activePart) return;
    setPartAssigning(true);
    try {
      await callOffAPI.assignPartSuppliers(
        activePart.id,
        Array.from(partSelectedSuppliers),
      );
      toast.success(`Updated suppliers for ${activePart.part_number}`);
      setPartDialogOpen(false);
      setActivePart(null);
      setPartSelectedSuppliers(new Set());
      fetchParts();
      fetchStats();
    } catch {
      toast.error('Failed to assign suppliers');
    } finally {
      setPartAssigning(false);
    }
  };

  const handleSupplierUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const response = await partsAPI.uploadSupplierAssignments(uploadFile);
      setUploadResult(response.data);
      fetchParts();
      fetchStats();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      const axiosErr = err as Record<string, Record<string, Record<string, string>>>;
      setUploadResult({
        total_rows: 0,
        assignments_created: 0,
        assignments_updated: 0,
        assignments_deactivated: 0,
        default_suppliers_set: 0,
        rows_without_supplier: 0,
        errors: [{ sheet: '', row: 0, error: axiosErr?.['response']?.['data']?.['error'] || message }],
        error_count: 1,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveSupplier = async (partId: number) => {
    try {
      await callOffAPI.clearPartSuppliers(partId);
      toast.success('Suppliers removed');
      fetchParts();
      fetchStats();
    } catch {
      toast.error('Failed to remove suppliers');
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total Parts</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.assigned}</p>
              <p className="text-xs text-muted-foreground">Assigned</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{stats.unassigned}</p>
              <p className="text-xs text-muted-foreground">Unassigned</p>
            </CardContent>
          </Card>
        </div>
      )}

      <PartRepositoryToolbar
        search={search}
        onSearchChange={setSearch}
        filterAssigned={filterAssigned}
        onFilterAssignedChange={setFilterAssigned}
        warehouse={warehouse}
        onWarehouseChange={setWarehouse}
        warehouses={warehouses}
        selectedCount={selectedParts.size}
        onAssignClick={() => setAssignDialogOpen(true)}
        onUploadClick={() => {
          setUploadOpen(true);
          setUploadFile(null);
          setUploadResult(null);
        }}
        onExport={runExport}
        exporting={exporting}
        exportDisabled={loading || totalCount === 0}
        pageSize={pageSize}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
      />

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>
            {Object.keys(filterParams).length > 0
              ? 'No parts match these filters'
              : 'No parts in the catalog'}
          </p>
        </div>
      ) : (
        <>
          <div className="border rounded-lg overflow-auto max-h-[calc(100vh-340px)]">
            <table className="w-full caption-bottom text-sm">
              <TableHeader className="sticky top-0 z-20 bg-muted shadow-[0_2px_4px_-2px_rgba(0,0,0,0.08)]">
                <TableRow className="hover:bg-muted border-b">
                  <TableHead className="w-10 font-semibold text-foreground">
                    <Checkbox
                      checked={selectedParts.size === items.length && items.length > 0}
                      onCheckedChange={toggleAll}
                    />
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">Part Number</TableHead>
                  <TableHead className="font-semibold text-foreground">Description</TableHead>
                  <TableHead className="font-semibold text-foreground">Alt Part No.</TableHead>
                  <TableHead className="font-semibold text-foreground text-right">Pallet Qty</TableHead>
                  <TableHead className="font-semibold text-foreground text-right">Load Qty</TableHead>
                  <TableHead className="font-semibold text-foreground">Suppliers</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((part) => (
                  <PartRepositoryRow
                    key={part.id}
                    part={part}
                    selected={selectedParts.has(part.id)}
                    onToggleSelect={togglePart}
                    onDoubleClick={handleRowDoubleClick}
                    onRemoveSuppliers={handleRemoveSupplier}
                  />
                ))}
              </TableBody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            itemLabel="parts"
          />
        </>
      )}

      {/* Bulk Assign Suppliers Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto scrollbar-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Assign Suppliers to {selectedParts.size} Part(s)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Search and select suppliers to assign:
            </p>
            <SupplierSearchList
              suppliers={suppliers}
              selected={selectedSuppliers}
              onToggle={(id) => {
                setSelectedSuppliers(prev => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id); else next.add(id);
                  return next;
                });
              }}
            />
            {selectedSuppliers.size > 0 && (
              <p className="text-sm text-muted-foreground">
                {selectedSuppliers.size} supplier(s) selected
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkAssign} disabled={assigning || selectedSuppliers.size === 0}>
              {assigning && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier-assignment Excel upload */}
      <SupplierAssignmentUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        uploadFile={uploadFile}
        onFileSelected={(f) => { setUploadFile(f); setUploadResult(null); }}
        uploading={uploading}
        uploadResult={uploadResult}
        onUpload={handleSupplierUpload}
        onDone={() => { setUploadOpen(false); setUploadResult(null); setUploadFile(null); }}
      />

      {/* Per-Part Assign Suppliers Dialog (double-click) */}
      <Dialog open={partDialogOpen} onOpenChange={setPartDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto scrollbar-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Manage Suppliers
            </DialogTitle>
          </DialogHeader>
          {activePart && (
            <div className="space-y-3 py-2">
              <div className="rounded-lg bg-muted/50 p-3">
                <p className="font-medium">{activePart.part_number}</p>
                <p className="text-sm text-muted-foreground">{activePart.description}</p>
              </div>
              <p className="text-sm text-muted-foreground">
                Search and select suppliers for this part:
              </p>
              <SupplierSearchList
                suppliers={suppliers}
                selected={partSelectedSuppliers}
                onToggle={(id) => {
                  setPartSelectedSuppliers(prev => {
                    const next = new Set(prev);
                    if (next.has(id)) next.delete(id); else next.add(id);
                    return next;
                  });
                }}
              />
              {partSelectedSuppliers.size > 0 && (
                <p className="text-sm text-muted-foreground">
                  {partSelectedSuppliers.size} supplier(s) selected
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPartDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePartAssign} disabled={partAssigning}>
              {partAssigning && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
